/* global DOMParser, FormData, fetch */
import Alpine from "alpinejs";
import collapse from "@alpinejs/collapse";
import focus from "@alpinejs/focus";
import {
	ArrowLeftRight,
	Award,
	BarChart3,
	BriefcaseBusiness,
	CheckCircle2,
	ChevronDown,
	ChevronRight,
	CircleGauge,
	ClipboardCheck,
	Code2,
	FileSearch,
	Gauge,
	GitBranch,
	Handshake,
	Layers,
	LifeBuoy,
	ListChecks,
	Mail,
	Menu,
	MonitorCheck,
	Network,
	Puzzle,
	RefreshCw,
	Rocket,
	SearchCheck,
	ServerCog,
	Settings,
	Shield,
	ShieldCheck,
	ShoppingCart,
	TrendingUp,
	Users,
	Workflow,
	Wrench,
	X,
	createIcons,
} from "lucide";

Alpine.plugin(collapse);
Alpine.plugin(focus);

const contactDrawerContentSelector = "[data-contact-drawer-content]";
const contactFormSelector = 'form[data-contact-form="drawer"]';
const contactFormErrorSelector = "[data-contact-form-error]";
const contactFormRetrySelector = "[data-contact-form-retry]";
const contactFormFeedbackSelector =
	"[data-contact-form-result], .has-error, [data-contact-form-error]";

export function createSiteHeaderState() {
	return {
		mobileMenuOpen: false,
		openMegadropId: null,
		openMobilePanelId: null,
		closeMegadropTimer: null,

		init() {
			this.escapeListener = (event) => {
				if (event.key === "Escape") {
					this.closeMegadrop();
					this.mobileMenuOpen = false;
				}
			};

			window.addEventListener("keydown", this.escapeListener);
		},

		destroy() {
			window.removeEventListener("keydown", this.escapeListener);
		},

		openMegadrop(id) {
			this.cancelMegadropClose();
			this.mobileMenuOpen = false;
			this.openMegadropId = id;
		},

		closeMegadrop() {
			this.cancelMegadropClose();
			this.openMegadropId = null;
		},

		cancelMegadropClose() {
			if (this.closeMegadropTimer) {
				window.clearTimeout(this.closeMegadropTimer);
				this.closeMegadropTimer = null;
			}
		},

		scheduleCloseMegadrop() {
			this.cancelMegadropClose();
			this.closeMegadropTimer = window.setTimeout(() => {
				this.openMegadropId = null;
				this.closeMegadropTimer = null;
			}, 120);
		},

		isMegadropOpen(id) {
			return this.openMegadropId === id;
		},

		handleMegadropFocusOut(event) {
			if (!event.currentTarget.contains(event.relatedTarget)) {
				this.closeMegadrop();
			}
		},

		toggleMobilePanel(id) {
			this.openMobilePanelId = this.openMobilePanelId === id ? null : id;
		},

		openContact() {
			this.closeMegadrop();
			this.mobileMenuOpen = false;
			window.dispatchEvent(new window.CustomEvent("triplewood:contact-open"));
		},
	};
}

Alpine.data("siteHeader", createSiteHeaderState);

export function extractContactDrawerContent(html) {
	const documentFromResponse = new DOMParser().parseFromString(html, "text/html");
	return (
		documentFromResponse.querySelector(contactDrawerContentSelector)?.innerHTML ??
		null
	);
}

export function createContactFormErrorMarkup() {
	return `
		<div data-contact-form-error class="mb-5 rounded-md bg-white p-6 text-petrol shadow-sm" role="alert" tabindex="-1">
			<h2 class="tw-type-card-heading text-orange">Das hat leider nicht geklappt.</h2>
			<p class="tw-type-body mt-3">Der Versand konnte gerade nicht abgeschlossen werden. Bitte versuchen Sie es erneut.</p>
			<button type="button" data-contact-form-retry class="tw-type-cta mt-5 inline-flex w-full items-center justify-center rounded-md bg-orange px-6 py-4 text-white transition-colors hocus:bg-petrol sm:w-auto">
				Erneut versuchen
			</button>
		</div>
	`;
}

export function createContactDrawerState() {
	return {
		open: false,
		isSubmitting: false,
		lastSubmittedForm: null,

		init() {
			this.openListener = () => {
				this.openDrawer();
			};

			window.addEventListener("triplewood:contact-open", this.openListener);

			this.escapeListener = (event) => {
				if (event.key === "Escape") {
					this.close();
				}
			};

			window.addEventListener("keydown", this.escapeListener);

			if (this.hasServerRenderedFeedback()) {
				this.openDrawer();
			}
		},

		destroy() {
			window.removeEventListener("triplewood:contact-open", this.openListener);
			window.removeEventListener("keydown", this.escapeListener);
		},

		openDrawer() {
			this.open = true;
			this.$nextTick(() => {
				this.$refs.closeButton?.focus();
			});
		},

		close() {
			this.open = false;
		},

		hasServerRenderedFeedback() {
			return Boolean(
				this.$refs.content?.querySelector(contactFormFeedbackSelector),
			);
		},

		async submitContactForm(event) {
			const form = event.target;

			if (!form.matches(contactFormSelector)) {
				return;
			}

			event.preventDefault();
			this.lastSubmittedForm = form;
			await this.sendContactForm(form);
		},

		async retryContactForm(event) {
			const retryButton = event.target.closest(contactFormRetrySelector);

			if (!retryButton) {
				return;
			}

			event.preventDefault();

			const form =
				this.lastSubmittedForm ??
				this.$refs.content?.querySelector(contactFormSelector);

			if (form) {
				await this.sendContactForm(form);
			}
		},

		async sendContactForm(form) {
			const formData = new FormData(form);
			this.setFormSubmitting(form, true);
			this.removeContactFormError();

			try {
				const response = await fetch(form.action || window.location.href, {
					method: form.method || "POST",
					body: formData,
					credentials: "same-origin",
					headers: {
						"X-Requested-With": "XMLHttpRequest",
					},
				});

				if (!response.ok) {
					throw new Error(`Contact form request failed: ${response.status}`);
				}

				const html = await response.text();
				const nextContent = extractContactDrawerContent(html);

				if (!nextContent) {
					throw new Error("Contact drawer content missing in response.");
				}

				this.$refs.content.innerHTML = nextContent;
				this.open = true;
				this.focusFeedback();
			} catch (error) {
				this.showContactFormError(error);
			} finally {
				this.setFormSubmitting(form, false);
			}
		},

		setFormSubmitting(form, isSubmitting) {
			this.isSubmitting = isSubmitting;
			form.toggleAttribute("aria-busy", isSubmitting);
			form.querySelectorAll("button, input, select, textarea").forEach((field) => {
				if (isSubmitting) {
					if (!field.disabled) {
						field.dataset.contactWasEnabled = "true";
					}
					field.disabled = true;
					return;
				}

				if (field.dataset.contactWasEnabled) {
					field.disabled = false;
					delete field.dataset.contactWasEnabled;
				}
			});
		},

		removeContactFormError() {
			this.$refs.content
				?.querySelectorAll(contactFormErrorSelector)
				.forEach((element) => element.remove());
		},

		showContactFormError(error) {
			void error;
			this.open = true;
			this.removeContactFormError();
			this.$refs.content?.insertAdjacentHTML(
				"afterbegin",
				createContactFormErrorMarkup(),
			);
			this.focusFeedback();
		},

		focusFeedback() {
			this.$nextTick(() => {
				this.$refs.content?.querySelector(contactFormFeedbackSelector)?.focus();
			});
		},
	};
}

Alpine.data("contactDrawer", createContactDrawerState);

window.Alpine = Alpine;
Alpine.start();

createIcons({
	icons: {
		ArrowLeftRight,
		Award,
		BarChart3,
		BriefcaseBusiness,
		CheckCircle2,
		ChevronDown,
		ChevronRight,
		CircleGauge,
		ClipboardCheck,
		Code2,
		FileSearch,
		Gauge,
		GitBranch,
		Handshake,
		Layers,
		LifeBuoy,
		ListChecks,
		Mail,
		Menu,
		MonitorCheck,
		Network,
		Puzzle,
		RefreshCw,
		Rocket,
		SearchCheck,
		ServerCog,
		Settings,
		Shield,
		ShieldCheck,
		ShoppingCart,
		TrendingUp,
		Users,
		Workflow,
		Wrench,
		X,
	},
});
